import { useState, useEffect, useRef, useCallback } from 'react';
import api from '../../services/api';
import { toast } from 'sonner';
import { MessageCircle, Send, Headset } from 'lucide-react';
import { buildSessionWsProtocol, resolveBackendWsBaseUrl } from '../../lib/wsAuth';

const getMessageText = (msg) => String(msg?.message ?? msg?.body ?? msg?.text ?? msg?.content ?? '').trim();

const normalizeMessage = (msg) => {
  if (!msg || typeof msg !== 'object') return null;
  return {
    ...msg,
    id: msg.id || msg._id || `chat_${msg.created_at || Date.now()}_${Math.random().toString(36).slice(2)}`,
    message: getMessageText(msg),
  };
};

const extractMessages = (payload) => {
  const raw = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.messages)
      ? payload.messages
      : Array.isArray(payload?.data?.messages)
        ? payload.data.messages
        : [];
  return raw.map(normalizeMessage).filter(Boolean);
};

const mergeMessages = (current, incoming) => {
  const temp = current.filter(m => String(m.id || '').startsWith('temp_'));
  const seen = new Set();
  return [...incoming, ...temp]
    .map(normalizeMessage)
    .filter(Boolean)
    .filter((m) => {
      if (seen.has(m.id)) return false;
      seen.add(m.id);
      return true;
    })
    .sort((a, b) => String(a.created_at || '').localeCompare(String(b.created_at || '')));
};

export default function ChatTab({ user, onRead, tierTheme }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const [supportChannels, setSupportChannels] = useState({});
  const sendingRef = useRef(false);
  const chatBoxRef = useRef(null);
  const wsRef = useRef(null);
  const inputRef = useRef(null);
  const reconnectTimer = useRef(null);
  const shouldReconnectRef = useRef(true);

  const scrollToEnd = useCallback(() => {
    requestAnimationFrame(() => {
      if (chatBoxRef.current) chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight;
    });
  }, []);

  // ── Support channels ──
  useEffect(() => {
    api.get('/api/public/settings/support-channels')
      .then(r => setSupportChannels(r.data.support_channels || {}))
      .catch(() => {});
  }, []);

  // ── WebSocket ──
  useEffect(() => {
    const connectWS = () => {
      const token = localStorage.getItem('session_token');
      if (!token) return;
      const wsBase = resolveBackendWsBaseUrl();
      const protocol = buildSessionWsProtocol(token);
      const ws = protocol
        ? new WebSocket(`${wsBase}/api/ws/chat/player`, [protocol])
        : new WebSocket(`${wsBase}/api/ws/chat/player`);
      ws.onopen = () => {
        setWsConnected(true);
        ws._ping = setInterval(() => { if (ws.readyState === 1) ws.send(JSON.stringify({ type: 'ping' })); }, 25000);
      };
      ws.onmessage = (event) => {
        try {
          const d = JSON.parse(event.data);
          if (d.type === 'new_message' && d.message) {
            const next = normalizeMessage(d.message);
            if (next) setMessages(p => p.some(m => m.id === next.id) ? p : [...p, next]);
            markRead();
          }
          if (d.type === 'message_sent' && d.message) {
            const next = normalizeMessage(d.message);
            if (!next) return;
            setMessages(p => {
              const c = p.filter(m => !m.id.startsWith('temp_'));
              return c.some(m => m.id === next.id) ? c : [...c, next];
            });
          }
        } catch {}
      };
      ws.onclose = () => {
        setWsConnected(false);
        clearInterval(ws._ping);
        if (shouldReconnectRef.current) {
          reconnectTimer.current = setTimeout(connectWS, 2000);
        }
      };
      ws.onerror = () => ws.close();
      wsRef.current = ws;
    };
    shouldReconnectRef.current = true;
    connectWS();
    return () => {
      shouldReconnectRef.current = false;
      clearTimeout(reconnectTimer.current);
      if (wsRef.current) wsRef.current.close();
    };
  }, []);

  useEffect(() => { fetchMessages(); markRead(); }, []);
  useEffect(() => {
    const i = setInterval(fetchMessages, wsConnected ? 30000 : 3000);
    return () => clearInterval(i);
  }, [wsConnected]);
  useEffect(() => { scrollToEnd(); }, [messages, scrollToEnd]);

  const markRead = async () => {
    try { await api.post('/api/chat/mark-read'); if (onRead) onRead(); } catch {}
  };

  const fetchMessages = async () => {
    try {
      const res = await api.get('/api/chat/messages?limit=200');
      const msgs = extractMessages(res.data);
      setMessages(p => mergeMessages(p, msgs));
    } catch {} finally { setLoading(false); }
  };

  const sendMessage = async () => {
    if (!input.trim() || sendingRef.current) return;
    sendingRef.current = true;
    setSending(true);
    const txt = input.trim();
    const tid = `temp_${Date.now()}`;
    setMessages(p => [...p, {
      id: tid, sender_id: user?.id, sender_role: 'player',
      sender_name: user?.username || 'You', message: txt,
      created_at: new Date().toISOString(),
    }]);
    setInput('');
    inputRef.current?.focus();
    if (wsRef.current?.readyState === 1) {
      wsRef.current.send(JSON.stringify({ type: 'message', message: txt }));
      sendingRef.current = false;
      setSending(false);
      return;
    }
    try {
      const res = await api.post('/api/chat/send', { message: txt });
      setMessages(p => { const c = p.filter(m => m.id !== tid); return [...c, res.data]; });
    } catch {
      toast.error('Failed');
      setMessages(p => p.filter(m => m.id !== tid));
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  };

  // ── External support channel icons ──
  const chs = [
    { key: 'whatsapp', label: 'WhatsApp', color: '#25D366', svg: 'M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38c1.45.79 3.08 1.21 4.74 1.21 5.46 0 9.91-4.45 9.91-9.91C21.95 6.45 17.5 2 12.04 2zm5.82 14.01c-.24.68-1.42 1.3-1.96 1.38-.5.07-1.13.1-1.82-.11-.42-.13-.95-.31-1.64-.61-2.89-1.24-4.77-4.17-4.91-4.36-.14-.19-1.14-1.52-1.14-2.9s.72-2.06.98-2.34c.26-.28.57-.35.76-.35.19 0 .38 0 .55.01.18.01.41-.07.65.49.24.57.82 2 .89 2.15.07.14.12.31.02.5-.1.19-.14.31-.28.47-.14.17-.3.37-.43.5-.14.14-.28.29-.12.57.17.28.74 1.22 1.58 1.97 1.09.97 2.01 1.27 2.29 1.41.28.14.45.12.61-.07.17-.19.71-.83.9-1.12.19-.28.38-.24.64-.14.26.1 1.64.77 1.92.91.28.14.47.21.54.33.07.12.07.68-.17 1.36z' },
    { key: 'telegram', label: 'Telegram', color: '#0088CC', svg: 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69.01-.03.01-.14-.07-.2-.08-.06-.19-.04-.27-.02-.12.03-1.99 1.27-5.62 3.72-.53.36-1.01.54-1.44.53-.47-.01-1.38-.27-2.06-.49-.83-.27-1.49-.42-1.43-.88.03-.24.37-.49 1.02-.75 3.99-1.73 6.65-2.87 7.97-3.44 3.8-1.58 4.59-1.86 5.1-1.87.11 0 .37.03.54.17.14.12.18.28.2.45-.01.06.01.24 0 .38z' },
    { key: 'messenger', label: 'Messenger', color: '#006AFF', svg: 'M12 2C6.36 2 2 6.13 2 11.7c0 2.91 1.2 5.42 3.15 7.18.16.15.26.36.27.58l.05 1.81c.02.63.66 1.03 1.24.78l2.02-.89c.17-.08.36-.1.55-.06.88.24 1.82.37 2.72.37 5.64 0 10-4.13 10-9.7C22 6.13 17.64 2 12 2zm5.89 7.41l-2.89 4.58c-.46.73-1.44.92-2.13.42l-2.3-1.73c-.17-.13-.41-.13-.58 0L7.5 14.67c-.31.24-.72-.13-.52-.47l2.89-4.58c.46-.73 1.44-.92 2.13-.42l2.3 1.73c.17.13.41.13.58 0l2.49-1.99c.31-.24.72.13.52.47z' },
  ];
  const channelHref = (ch, link) => {
    if (!link) return '';
    const value = String(link).trim();
    if (!value) return '';
    if (ch.key === 'whatsapp') return `https://wa.me/${value.replace(/[^0-9]/g, '')}`;
    if (ch.key === 'telegram') return value.startsWith('http') ? value : `https://t.me/${value.replace('@', '')}`;
    if (ch.key === 'messenger') return value.startsWith('http') ? value : `https://m.me/${value}`;
    return value;
  };

  const activeChannels = chs
    .map(ch => {
      const link = supportChannels[`${ch.key}_link`] || supportChannels[ch.key];
      return { ...ch, link, href: channelHref(ch, link) };
    })
    .filter(ch => ch.href);
  const hasChs = activeChannels.length > 0;

  // Keep legacy/body-only messages and attachment-only history visible.
  const visibleMessages = messages.filter(m => getMessageText(m) || (m?.attachments || []).length > 0);

  // Helper: group consecutive messages by same sender within 3 minutes
  const isSameGroupAsPrev = (idx) => {
    if (idx === 0) return false;
    const prev = visibleMessages[idx - 1];
    const cur = visibleMessages[idx];
    if (prev.sender_role !== cur.sender_role) return false;
    const prevT = new Date(prev.created_at).getTime();
    const curT = new Date(cur.created_at).getTime();
    return (curT - prevT) < 3 * 60 * 1000;
  };

  const formatTime = (ts) => new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  return (
    <div data-testid="chat-tab" className="flex flex-col h-full w-full" style={{ overflow: 'hidden' }}>
      {/* ── Permanent Chat Header (WhatsApp-style) ── */}
      <div
        className="flex items-center justify-between gap-2 px-3 py-2.5 flex-shrink-0 rounded-t-2xl"
        style={{ background: 'linear-gradient(135deg, rgba(212,175,55,0.12), rgba(212,175,55,0.04))', borderBottom: '1px solid rgba(212,175,55,0.18)' }}
        data-testid="chat-header"
      >
        <div className="flex items-center gap-2.5 min-w-0 flex-1">
          <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #D4AF37, #F5D76E)' }}>
            <Headset className="w-5 h-5 text-black" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-bold text-white leading-tight">Live Support</p>
            <p className="flex items-center gap-1 text-[10px] leading-tight mt-0.5">
              <span className={`w-1.5 h-1.5 rounded-full ${wsConnected ? 'bg-green-400' : 'bg-yellow-400 animate-pulse'}`} />
              <span className={wsConnected ? 'text-green-400' : 'text-yellow-400'}>{wsConnected ? 'Online — reply in minutes' : 'Connecting…'}</span>
            </p>
          </div>
        </div>
        {hasChs && (
          <div className="flex items-center gap-1.5 flex-shrink-0" data-testid="external-support-channels">
            {activeChannels.map(ch => {
              return (
                <a key={ch.key} href={ch.href} target="_blank" rel="noopener noreferrer"
                  className="flex items-center justify-center w-9 h-9 rounded-full transition-transform hover:scale-110 active:scale-95"
                  style={{ background: `${ch.color}22`, border: `1.5px solid ${ch.color}55` }}
                  data-testid={`support-${ch.key}`}>
                  <svg viewBox="0 0 24 24" width="18" height="18" fill={ch.color}>
                    <path d={ch.svg} />
                  </svg>
                </a>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Messages area (WhatsApp-style chat background) ── */}
      <div
        ref={chatBoxRef}
        className="flex-1 overflow-y-auto px-3 py-3"
        style={{
          background: '#0d1117',
          backgroundImage: 'radial-gradient(rgba(212,175,55,0.04) 1px, transparent 1px)',
          backgroundSize: '24px 24px',
        }}
        data-testid="chat-messages"
      >
        {loading ? (
          <div className="h-full flex items-center justify-center"><div className="spinner" /></div>
        ) : visibleMessages.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-gray-500 px-6 text-center">
            <MessageCircle className="w-12 h-12 mb-3 opacity-25" />
            <p className="text-base font-semibold text-gray-300">Start a conversation</p>
            <p className="text-xs mt-1">We typically reply within a few minutes</p>
            {hasChs && (
              <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-2 w-full max-w-xl" data-testid="support-channel-cards">
                {activeChannels.map(ch => (
                  <a
                    key={ch.key}
                    href={ch.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 rounded-xl px-3 py-3 text-sm font-semibold text-white transition-transform hover:scale-[1.02] active:scale-95"
                    style={{ background: `${ch.color}20`, border: `1px solid ${ch.color}66` }}
                    data-testid={`support-card-${ch.key}`}
                  >
                    <svg viewBox="0 0 24 24" width="18" height="18" fill={ch.color}>
                      <path d={ch.svg} />
                    </svg>
                    {ch.label}
                  </a>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-0.5">
            {visibleMessages.map((msg, idx) => {
              const isMe = msg.sender_role === 'player';
              const grouped = isSameGroupAsPrev(idx);
              const showAvatar = !isMe && !grouped;
              const initial = (msg.sender_name || 'S').charAt(0).toUpperCase();
              return (
                <div
                  key={msg.id}
                  className={`flex items-end gap-1.5 ${isMe ? 'justify-end' : 'justify-start'} ${grouped ? 'mt-0.5' : 'mt-2.5'}`}
                >
                  {/* Support avatar (left) */}
                  {!isMe && (
                    <div className="w-7 h-7 flex-shrink-0">
                      {showAvatar && (
                        <div className="w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold text-black" style={{ background: 'linear-gradient(135deg, #D4AF37, #F5D76E)' }}>
                          {initial}
                        </div>
                      )}
                    </div>
                  )}
                  <div className={`max-w-[78%] flex flex-col ${isMe ? 'items-end' : 'items-start'}`}>
                    {!isMe && !grouped && <p className="text-[10px] font-bold text-[#D4AF37] mb-0.5 ml-1">{msg.sender_name || 'Support'}</p>}
                    <div
                      className={`${msg.id.startsWith('temp_') ? 'opacity-60' : ''} inline-flex items-end gap-1.5 px-2.5 py-1.5`}
                      style={isMe ? {
                        background: 'linear-gradient(135deg, #D4AF37, #F5D76E)',
                        color: '#000',
                        borderRadius: grouped ? '16px 6px 6px 16px' : '16px 16px 4px 16px',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
                      } : {
                        background: '#1e2330',
                        color: '#fff',
                        borderRadius: grouped ? '6px 16px 16px 6px' : '4px 16px 16px 16px',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.25)',
                        border: '1px solid rgba(255,255,255,0.04)',
                      }}
                    >
                      <span className="text-[14px] leading-snug whitespace-pre-wrap break-words">{getMessageText(msg) || 'Photo'}</span>
                      <span className={`text-[9px] leading-none pb-0.5 flex-shrink-0 ${isMe ? 'text-black/40' : 'text-gray-500'}`}>
                        {formatTime(msg.created_at)}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Input bar (Messenger-style pill) ── */}
      <div className="flex items-end gap-2 px-3 py-2.5 flex-shrink-0" style={{ background: '#0a0e17', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <div className="flex-1 min-w-0 rounded-full pl-4 pr-1.5 py-1 flex items-end gap-2" style={{ background: '#1e2330', border: '1px solid rgba(255,255,255,0.08)' }}>
          <textarea
            ref={inputRef}
            rows={1}
            value={input}
            onChange={e => {
              setInput(e.target.value);
              e.target.style.height = 'auto';
              e.target.style.height = `${Math.min(e.target.scrollHeight, 110)}px`;
            }}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
            onFocus={() => setTimeout(scrollToEnd, 300)}
            placeholder="Type a message…"
            disabled={sending}
            className="flex-1 min-w-0 bg-transparent text-white text-[15px] leading-snug py-2 outline-none resize-none placeholder:text-gray-500"
            style={{ maxHeight: 110 }}
            data-testid="player-chat-input"
          />
        </div>
        <button
          onClick={sendMessage}
          disabled={!input.trim() || sending}
          className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 transition-all disabled:opacity-40 active:scale-90"
          style={{ background: input.trim() ? 'linear-gradient(135deg, #D4AF37, #F5D76E)' : '#1e2330', color: '#000' }}
          data-testid="player-chat-send"
          aria-label="Send message"
        >
          {sending ? <div className="spinner w-5 h-5" /> : <Send className="w-5 h-5" />}
        </button>
      </div>
    </div>
  );
}
